import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import FinalStep from "./FinalStep";
import PersonStep from "./PersonStep";
import DocumentsStep from "./DocumentsStep";
import { Steps, Button, Row, Col, Card, message, Modal, List, InputNumber, Typography, Space, Tooltip } from "antd";
import { ExclamationCircleOutlined, EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CloseStep from "./CloseStep";
import { useUpdateCartransfer } from "../../../hooks/useUpdateCartransfer";
import { setRepertory } from "../../../features/cartransferSlice";
import { canProceedToNextStep } from "../../../utils/stepValidation";
import { setCartransfer as apiSetCartransfer } from "../../../api/cartransferService";

const { Step } = Steps;

//enable rules
//steps 1,2
//enabled until there is signed uploaded img
//step 3
//disabled until there is buyer, seller and documents
//disabled if signed contract is uploaded
//step 4
//disabled until contract is generated

const RepertoryEditor = ({ cartransferId, repertory, repertoryYear }) => {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(repertory);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value || value === repertory) { setEditing(false); return; }
    setSaving(true);
    try {
      await apiSetCartransfer(cartransferId, { repertory: value, repertoryYear });
      dispatch(setRepertory({ repertory: value, repertoryYear }));
      message.success(`Repertorio cambiado a ${value}-${repertoryYear}`);
      setEditing(false);
    } catch (e) {
      message.error(e?.response?.data?.message || "Repertorio ya existe en este año");
      setValue(repertory);
    }
    setSaving(false);
  };

  if (!repertory) return null;

  return (
    <Space size={4}>
      <Typography.Text strong style={{ fontSize: 13 }}>
        Repertorio:
      </Typography.Text>
      {editing ? (
        <>
          <InputNumber
            size="small"
            min={1}
            value={value}
            onChange={setValue}
            style={{ width: 90 }}
            onPressEnter={save}
            autoFocus
          />
          <Tooltip title="Guardar"><Button size="small" type="primary" icon={<CheckOutlined />} loading={saving} onClick={save} /></Tooltip>
          <Tooltip title="Cancelar"><Button size="small" icon={<CloseOutlined />} onClick={() => { setValue(repertory); setEditing(false); }} /></Tooltip>
        </>
      ) : (
        <>
          <Typography.Text strong style={{ fontSize: 13, color: "#1677ff" }}>
            {repertory}-{repertoryYear}
          </Typography.Text>
          <Tooltip title="Editar repertorio">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setValue(repertory); setEditing(true); }} />
          </Tooltip>
        </>
      )}
    </Space>
  );
};

const CartransferCreateSteps = ({ assignRepertory, discard, saveChanges, loading }) => {
  const cartransfer = useSelector((state) => state.cartransfer);
  const [current, setCurrent] = useState(0);
  const dispatch = useDispatch();
  const updateCartransfer = useUpdateCartransfer();
  const steps = [
    {
      title: "Datos personas",
      content: <PersonStep />,
      disabled: cartransfer.folder_contract_signed,
    },
    {
      title: "Documentos",
      content: <DocumentsStep />,
      disabled: cartransfer.folder_contract_signed,
    },
    {
      title: "Contrato",
      content: <FinalStep />,
      disabled:
        cartransfer.folder_contract_signed ||
        !(cartransfer.buyers?.[0]?.rut || cartransfer.buyer?.rut) ||
        !(cartransfer.sellers?.[0]?.rut || cartransfer.seller?.rut),
    },
    {
      title: "Cierre repertorio",
      content: <CloseStep />,
      disabled: !cartransfer.folder_contract_original,
    },
  ];

  const next = async () => {
    // Validar campos obligatorios del paso actual antes de avanzar (solo para pasos 1-3)
    if (current < 3) {
      const { canProceed, validation } = canProceedToNextStep(current + 1, cartransfer);
      
      if (!canProceed) {
        // Mostrar modal con campos faltantes
        Modal.error({
          title: `Paso ${current + 1}: Campos obligatorios faltantes`,
          icon: <ExclamationCircleOutlined />,
          content: (
            <div>
              <p>Para continuar al siguiente paso, debes completar los siguientes campos obligatorios:</p>
              <List
                size="small"
                dataSource={validation.missingFields}
                renderItem={(item) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    • {item}
                  </List.Item>
                )}
              />
            </div>
          ),
          okText: 'Entendido',
          width: 500,
        });
        return;
      }
    }
    
    // Si estamos pasando del paso 1 al paso 2 y no hay repertorio, asignar uno automáticamente
    if (current === 0 && !cartransfer.repertory) {
      try {
        const result = await updateCartransfer.mutateAsync({
          id: cartransfer.id,
          data: { status: "creado" }
        });
        const { repertory, repertoryYear } = result.data;

        dispatch(
          setRepertory({
            repertory,
            repertoryYear,
          })
        );
      } catch (error) {
        console.error('Error assigning repertory:', error);
      }
    }
    
    // Guardado automático al pasar de cada paso (1->2, 2->3, 3->4)
    try {
      const payload = {
        ...(cartransfer.buyers?.length > 0 ? { buyers: cartransfer.buyers } : {}),
        ...(cartransfer.sellers?.length > 0 ? { sellers: cartransfer.sellers } : {}),
        ...(cartransfer.buyer ? { buyer: cartransfer.buyer } : {}),
        ...(cartransfer.seller ? { seller: cartransfer.seller } : {}),
        ...(cartransfer.background ? { background: cartransfer.background } : {}),
        ...(cartransfer.permission ? { permission: cartransfer.permission } : {}),
        ...(cartransfer.contract ? { contract: cartransfer.contract } : {}),
      };
      await updateCartransfer.mutateAsync({ id: cartransfer.id, data: payload });
      // message.success('Cambios guardados'); // opcional mostrar
    } catch (error) {
      console.error('Error auto-saving step data:', error);
      message.error('No se pudieron guardar los cambios del paso');
    }
    
    setCurrent(current + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Card style={{ margin: 10 }} extra={
        <Space size={8}>
          {!cartransfer.repertory ? (
            <>
              <Button size="small" onClick={discard}>Descartar borrador</Button>
              <Button size="small" type="primary" onClick={assignRepertory}>Abrir repertorio</Button>
            </>
          ) : (
            <>
              <RepertoryEditor
                cartransferId={cartransfer.id}
                repertory={cartransfer.repertory}
                repertoryYear={cartransfer.repertoryYear}
              />
              <Button size="small" onClick={saveChanges} loading={loading}>Guardar Cambios</Button>
            </>
          )}
        </Space>
      }>
        <Row>
          <Col span={24}>
            <Steps
              current={current}
              onChange={(current) => setCurrent(current)}
            >
              {steps.map((item) => (
                <Step
                  key={item.title}
                  title={item.title}
                  disabled={item.disabled}
                />
              ))}
            </Steps>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col span={24} style={{ padding: 10 }}>
          {steps[current].content}
        </Col>
      </Row>

      <Card style={{ margin: 10 }}>
        <Row gutter={16}>
          <Col>
            {current > 0 && <Button onClick={() => prev()}>Anterior</Button>}
          </Col>
          <Col>
            {current < steps.length - 1 && (
              <Button type="primary" onClick={() => next()}>
                Siguiente
              </Button>
            )}
            {current === steps.length - 1 && <Button>Salir</Button>}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default CartransferCreateSteps;
